// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import type { Paths } from '@shared/data-access/interfaces/routes/paths';
import {
  type FC,
  Fragment,
  type MouseEventHandler,
  type ReactElement,
  type ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { AppLink } from '@advisor-app/routes/AppLink';
import { ReactComponent as LogoCitec } from '@advisor-app/assets/img/logo-citec.svg';
import { ReactComponent as LogoNuvara } from '@shared/ui-kit/assets/img/logo-nuvara360.svg';
import { AppIcon, type SvgIconName } from '@shared/ui-kit/components/app-icon';
import styles from './navigation-sidebar.module.css';
import { AccountLogo } from './account-logo';
import { classNames, getTextBeforeSymbol } from '@shared/ui-kit/utils';
import { useAppSelector } from '@advisor-app/store/store';
import {
  selectAccount,
  selectAccountId,
  selectBrokerConnected,
} from '@advisor-app/store/slices/broker';
import { NavLink, useLocation } from 'react-router';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Tooltip,
} from '@mui/material';
import { resetTrades, selectTraderTrades } from '@advisor-app/store/slices/trader';
import { deleteConfirmationPopup } from '@shared/ui-kit/utils/delete-confirmation-popup';
import { useDispatch } from 'react-redux';
import { DeleteButton } from '@shared/ui-kit/components/delete-button';
import { ClientProfileSelector } from '@shared/advisory/client-profile/components/client-profile-selector';
import { ClientProfileLink } from '@shared/advisory/client-profile/components/client-profile-link';
import { useTranslation } from 'react-i18next';
import { selectClient, selectClientId } from '@shared/data-access/slices/clients';
import { selectUserId } from '@shared/data-access/slices/user';
import { getBrandDomainByHostname } from '@shared/environment';
import { useAdvisorRole } from '@advisor-app/hooks';
import { isDefined } from '@shared/utils';

type NavigationButtonElement = {
  id: string;
  type: 'button';
  component: ReactElement<any, any>;
  separator?: boolean;
  hidden?: boolean;
  gap?: boolean;
};

type NavigationLinkElement = {
  id: string;
  type: 'link';
  title: string;
  to: Paths;
  children?: Array<
    Pick<NavigationLinkElement, 'title' | 'to'> & {
      icon?: SvgIconName;
    }
  >;
  icon: SvgIconName;
  separator?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  description?: string;
  adornment?: ReactNode;
  gap?: boolean;
  parent?: boolean;
};

type NavigationSectionTitle = {
  id: string;
  type: 'section-title';
  title: string;
  hidden?: boolean;
};

export type NavigationLink =
  | NavigationLinkElement
  | NavigationButtonElement
  | NavigationSectionTitle;

export interface NavigationSidebarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const NavigationSidebar: FC<NavigationSidebarProps> = ({
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation();

  const domain = useMemo(
    () =>
      typeof window === 'undefined'
        ? 'citec'
        : getBrandDomainByHostname(window.location.hostname),
    [],
  );

  const dispatch = useDispatch();

  const selectedClientId = useAppSelector(selectClientId);
  const activeClient = useAppSelector(selectClient);
  const userId = useAppSelector(selectUserId);
  const hasSelectedClient = isDefined(activeClient) && activeClient.id !== 0;

  const isActiveClientOwnedByUser = useMemo(
    () =>
      isDefined(activeClient) &&
      isDefined(userId) &&
      activeClient.citec_user_id === userId,
    [activeClient, userId],
  );

  const isBrokerConnected = useAppSelector(selectBrokerConnected);
  const accountId = useAppSelector(selectAccountId);
  const account = useAppSelector(selectAccount);

  const [expandedAccordions, setExpandedAccordions] = useState<
    Record<string, boolean>
  >({
    'aggregation-dashboard': true,
  });

  const onDrawerChange = useCallback(
    () => onOpenChange(!open),
    [onOpenChange, open],
  );

  const accountName = useMemo(
    () => account.name?.split(' ').slice(0, 2).join(' ') || '',
    [account.name],
  );

  const showClientSection = useMemo(
    () => (isBrokerConnected && !!accountId) || hasSelectedClient,
    [accountId, hasSelectedClient, isBrokerConnected],
  );

  const isClientProfileHidden = useMemo(
    () => selectedClientId === null,
    [selectedClientId],
  );

  const disabledBrokerDashboard = useMemo(() => {
    return !accountId;
  }, [accountId]);

  const disabledAggregationDashboard = useMemo(
    () => selectedClientId === null,
    [selectedClientId],
  );

  const showBrokerDashboard = useMemo(() => {
    return isBrokerConnected && !!accountId;
  }, [isBrokerConnected, accountId]);

  const trades = useAppSelector(selectTraderTrades);
  const { canUseTrader } = useAdvisorRole();

  const location = useLocation();

  const onDeleteTrades: MouseEventHandler = useCallback(
    e => {
      e.preventDefault();

      deleteConfirmationPopup(
        {
          title: t('common.delete-all-trades', { ns: 'common' }),
          text: t('common.delete-all-trades-confirmation', { ns: 'common' }),
          confirmMessage: t('common.all-trades-deleted', { ns: 'common' }),
        },
        isDeleted => {
          /* istanbul ignore if */
          if (!isDeleted) return;
          dispatch(resetTrades());
        },
        t,
      );
    },
    [dispatch, t],
  );

  const renderSectionTitle = (props: NavigationSectionTitle, key: number) => (
    <span
      key={key}
      className={classNames(styles['link-section-title'], {
        [styles['link-hidden']]: props.hidden,
      })}
      aria-hidden={!open}
      data-testid="navigation-sidebar-section-title"
    >
      {props.title}
    </span>
  );

  const renderLink = (
    props: NavigationLinkElement,
    key: number,
    open: boolean,
    location: any,
  ) => (
    <Fragment key={key}>
      {props.gap && <hr className={classNames(styles['list-divider'], '!mt-20')} />}
      {props.children?.length && open ? (
        <Accordion
          key={`${props.id}-${selectedClientId}-${accountId}`}
          classes={{ root: styles.accordion }}
          aria-disabled={props.disabled}
          disableGutters
          data-testid={`navigation-sidebar-accordion-${props.id}`}
          expanded={
            props.disabled
              ? false
              : expandedAccordions[props.id] ??
                (location.pathname.includes(props.to) && !props.disabled)
          }
          onChange={(_, isExpanded) => {
            if (!props.disabled) {
              setExpandedAccordions(prev => ({
                ...prev,
                [props.id]: isExpanded,
              }));
            }
          }}
          disabled={props.disabled}
        >
          <AccordionSummary
            id={props.to}
            classes={{
              root: classNames(styles['accordion-summary'], {
                [styles['accordion-summary-active']]: location.pathname.includes(
                  props.to,
                ),
              }),
              content: classNames(styles['accordion-summary-content'], {
                [styles['accordion-summary-content-row']]: props.parent,
              }),
            }}
            aria-disabled={props.disabled}
          >
            {props.parent ? (
              <NavLink
                to={props.to}
                end
                onClick={event => event.stopPropagation()}
                className={({ isActive }) =>
                  classNames(
                    styles['accordion-panel-link'],
                    isActive && styles['accordion-panel-link-active'],
                  )
                }
                data-aria-expanded={open}
              >
                <AppIcon icon={props.icon} size={16} />
                <span className={styles['link-title']}>{props.title}</span>
              </NavLink>
            ) : (
              <span className="flex flex-row items-center gap-x-4">
                <AppIcon icon={props.icon} size={16} />
                <span className={styles['link-title']}>{props.title}</span>
              </span>
            )}
          </AccordionSummary>
          {!props.disabled && (
            <AccordionDetails
              classes={{ root: classNames(styles['accordion-details']) }}
            >
              {props.children.map(({ title, to, icon }) => (
                <NavLink
                  to={to}
                  key={to}
                  className={({ isActive }) =>
                    classNames(styles.link, {
                      [styles['link-active']]: isActive,
                    })
                  }
                  data-aria-expanded={open}
                >
                  {icon && <AppIcon icon={icon} size={16} />}
                  <span className={styles['link-title']}>{title}</span>
                </NavLink>
              ))}
            </AccordionDetails>
          )}
        </Accordion>
      ) : (
        <Tooltip
          title={
            props.disabled
              ? props.id === 'broker-dashboard'
                ? t('common.no-broker-account-selected', { ns: 'common' })
                : props.id === 'aggregation-dashboard'
                ? t('common.no-client-profile-selected', { ns: 'common' })
                : props.title
              : props.title
          }
          placement="right"
        >
          <span>
            <NavLink
              to={props.to}
              className={({ isActive }) =>
                classNames(styles.link, {
                  [styles['link-active']]: isActive,
                  [styles['link-hidden']]: props.hidden,
                })
              }
              key={props.to}
              data-aria-expanded={open}
              aria-disabled={props.disabled}
              end={props.to === '/'}
              data-testid={`navigation-sidebar-link-${props.id}`}
            >
              {open ? (
                <AppIcon icon={props.icon} size={16} />
              ) : (
                <AppIcon icon={props.icon} size={16} />
              )}
              <span className={styles['link-title']} aria-hidden={!open}>
                {props.title}&nbsp;
                {open && props.description}
              </span>
              {open && props.adornment}
            </NavLink>
          </span>
        </Tooltip>
      )}
      {props.separator || (props.gap && <hr className={styles['list-divider']} />)}
    </Fragment>
  );

  const renderButton = (
    props: NavigationButtonElement,
    key: number,
    open: boolean,
  ) => (
    <Fragment key={key}>
      {props.gap && <hr className={styles['list-divider']} />}
      <div
        className={classNames(styles.link, {
          [styles['link-hidden']]: props.hidden,
        })}
        data-aria-expanded={open}
        data-testid={`navigation-sidebar-button-${props.id}`}
      >
        {props.component}
      </div>
      {props.separator || (props.gap && <hr className={styles['list-divider']} />)}
    </Fragment>
  );

  const navigationLinks = useMemo<NavigationLink[]>(
    () => [
      {
        id: 'accounts',
        type: 'section-title',
        title: t('common.accounts', { ns: 'common' }),
      },
      {
        id: 'all-accounts',
        title: t('common.all-accounts', { ns: 'common' }),
        to: '/',
        children: [],
        icon: 'Account',
        separator: true,
        type: 'link',
      },
      {
        id: 'client-profile',
        type: 'section-title',
        title: t('common.client', { ns: 'common' }),
      },
      {
        id: 'client-profile-button',
        type: 'button',
        component: <ClientProfileSelector expanded={open} />,
      },
      ...((showClientSection
        ? [
            {
              id: 'client-profile-link',
              type: 'button',
              component: (
                <ClientProfileLink expanded={open} hidden={isClientProfileHidden} />
              ),
              hidden: isClientProfileHidden,
            },
            ...(showBrokerDashboard
              ? [
                  {
                    id: 'broker-dashboard',
                    title: t('common.broker-dashboard', { ns: 'common' }),
                    to: '/dashboard',
                    children: [],
                    icon: 'Dashboard',
                    type: 'link',
                    disabled: disabledBrokerDashboard,
                  } as NavigationLinkElement,
                ]
              : []),
            ...(selectedClientId !== null
              ? [
                  {
                    id: 'aggregation-dashboard',
                    type: 'link',
                    icon: 'Dashboard',
                    to: '/aggregation',
                    title: t('common.aggregation-dashboard', { ns: 'common' }),
                    parent: true,
                    children: [
                      {
                        title: t('glossary.investment', { ns: 'glossary' }),
                        to: '/aggregation/investment',
                        icon: 'TrendingUp',
                      },
                      ...(isActiveClientOwnedByUser
                        ? [
                            {
                              title: t('glossary.illiquids', { ns: 'glossary' }),
                              to: '/aggregation/illiquids',
                              icon: 'Layers' as const,
                            },
                          ]
                        : []),
                      {
                        title: t('glossary.loans', { ns: 'glossary' }),
                        to: '/aggregation/loans',
                        icon: 'CreditCard',
                      },
                      {
                        title: t('glossary.cashflow', { ns: 'glossary' }),
                        to: '/aggregation/cashflow',
                        icon: 'DollarSign',
                      },
                      {
                        title: t('glossary.transactions', { ns: 'glossary' }),
                        to: '/aggregation/transactions',
                        icon: 'Receipt',
                      },
                    ],
                    separator: true,
                    disabled: disabledAggregationDashboard,
                  } as NavigationLinkElement,
                  {
                    id: 'banks',
                    title: t('glossary.banks', { ns: 'glossary' }),
                    to: '/banks',
                    children: [],
                    icon: 'Landmark',
                    type: 'link',
                  } as NavigationLinkElement,
                  ...(canUseTrader
                    ? [
                        {
                          id: 'operations',
                          title: t('common.operations', { ns: 'common' }),
                          to: '/operations',
                          icon: 'ShoppingCart',
                          type: 'link',
                          description:
                            trades.length > 0 ? `(${trades.length})` : undefined,
                          adornment:
                            trades.length > 0 ? (
                              <DeleteButton
                                onClick={onDeleteTrades}
                                toolTipTitle={t(
                                  'common.delete-all-trades-tooltip',
                                  {
                                    ns: 'common',
                                  },
                                )}
                                data-testid="navigation-sidebar-delete-all-trades"
                              />
                            ) : null,
                        } as NavigationLinkElement,
                      ]
                    : []),
                  ...(isActiveClientOwnedByUser
                    ? [
                        {
                          id: 'documents',
                          title: t('glossary.documents', { ns: 'glossary' }),
                          to: '/documents',
                          children: [],
                          icon: 'FileText',
                          type: 'link',
                        } as NavigationLinkElement,
                      ]
                    : []),
                ]
              : []),
          ]
        : []) as NavigationLink[]),

      {
        id: 'tools',
        type: 'section-title',
        title: t('common.tools', { ns: 'common' }),
      },
      {
        id: 'research',
        title: t('common.research', { ns: 'common' }),
        to: '/research',
        children: [],
        icon: 'Research',
        type: 'link',
      },
      {
        id: 'ai-builder',
        title: t('common.ai-builder', { ns: 'common' }),
        to: '/ai-builder',
        type: 'link',
        children: [
          {
            title: t('common.stocks', { ns: 'common' }),
            to: '/ai-builder/stock',
          },
          {
            title: t('common.etfs', { ns: 'common' }),
            to: '/ai-builder/etf',
          },
          {
            title: t('common.mutual-funds', { ns: 'common' }),
            to: '/ai-builder/mutual-fund',
          },
        ],
        icon: 'AiBuilder',
      },
      {
        id: 'analysis',
        title: t('common.analysis', { ns: 'common' }),
        to: '/analysis',
        type: 'link',
        children: [
          {
            title: t('common.stocks', { ns: 'common' }),
            to: '/analysis/stock',
          },
          {
            title: t('common.etfs', { ns: 'common' }),
            to: '/analysis/etf',
          },
          {
            title: t('common.mutual-funds', { ns: 'common' }),
            to: '/analysis/mutual-fund',
          },
        ],
        icon: 'Analysis',
      },
      {
        id: 'rebalance',
        title: t('common.rebalance', { ns: 'common' }),
        to: '/rebalance',
        icon: 'Rebalance',
        type: 'link',
      },
    ],
    [
      t,
      showClientSection,
      open,
      isClientProfileHidden,
      showBrokerDashboard,
      disabledBrokerDashboard,
      disabledAggregationDashboard,
      selectedClientId,
      isActiveClientOwnedByUser,
      trades.length,
      onDeleteTrades,
      canUseTrader,
    ],
  );

  return (
    <>
      <div
        aria-hidden="true"
        className={styles.backdrop}
        onClick={() => onOpenChange(false)}
        data-aria-expanded={open}
        data-testid="navigation-sidebar-backdrop"
      />

      <aside
        data-aria-expanded={open}
        className={classNames(styles.container, 'group')}
        data-scrollable
        data-testid="navigation-sidebar"
      >
        <div className={styles.header}>
          <AppLink
            to="/"
            className="hidden group-data-[aria-expanded=true]:block"
            data-testid="navigation-sidebar-logo"
          >
            {domain === 'nuvara' ? <LogoNuvara /> : <LogoCitec />}
          </AppLink>
          <button
            onClick={onDrawerChange}
            data-testid="navigation-sidebar-drawer-button"
          >
            <AppIcon icon="Menu" color="light" />
          </button>
        </div>
        <hr className={styles.divider} />
        <div>
          {accountName !== '' && (
            <>
              <figure
                className="mb-[18px] mt-7 flex items-center justify-center gap-x-3 px-3 data-[aria-expanded=true]:justify-start"
                data-aria-expanded={open}
                data-testid="navigation-sidebar-account-logo"
              >
                <AccountLogo className={classNames({ 'ml-3': open })} />
                <figcaption
                  className="hidden whitespace-nowrap font-poppins text-lg capitalize leading-6 text-white data-[aria-expanded=true]:block"
                  data-aria-expanded={open}
                >
                  {getTextBeforeSymbol(accountName)}
                </figcaption>
              </figure>
              <hr className={styles['list-divider']} />
            </>
          )}
          <nav
            className={styles.navigation}
            data-aria-expanded={open}
            data-testid="navigation-sidebar-navigation"
          >
            {navigationLinks.map((props, index) => {
              switch (props.type) {
                case 'section-title':
                  return renderSectionTitle(props, index);
                case 'link':
                  return renderLink(props, index, open, location);
                case 'button':
                  return renderButton(props, index, open);
                default:
                  return null;
              }
            })}
          </nav>
        </div>
      </aside>
    </>
  );
};
